import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { AppDialog, FilterBar, useFilterBarStyles } from '../ui';
import { Stories } from '../../store/slices';
import {
    STORY_OCCASION_OPTIONS, STORY_PRODUCT_OPTIONS, STORY_PUBLISHED_OPTIONS, STORY_RECIPIENT_OPTIONS, STORY_STATUS_OPTIONS,
} from '../../lib/storyOptions';

const GENERAL_KEYS = ['Published', 'Status', 'Occasion', 'Recipient', 'Product'];

interface StoriesTagFiltersProps {
    queryParams: any;
    loadStories: (params: any) => void;
    loading: boolean;
    visibleFilters: string[];
    setVisibleFilters: React.Dispatch<React.SetStateAction<string[]>>;
    storyTags: any;
}

/**
 * Filters of the Stories tags page. Changes are held locally and sent together with "Apply"
 * (`tags` is the comma-joined list of every checked tag id). "Edit filters" chooses which general
 * filters and tag categories are visible, both as filters and as table columns.
 */
export default function StoriesTagFilters({ queryParams, loadStories, loading, visibleFilters, setVisibleFilters, storyTags }: StoriesTagFiltersProps) {
    const dispatch = useDispatch();
    const classes = useFilterBarStyles();
    const [selectedTags, setSelectedTags] = useState<Record<string, number[]>>({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingFilters, setPendingFilters] = useState({
        isPublished: queryParams?.isPublished || '',
        statusId: queryParams?.statusId || '',
        occasionId: queryParams?.occasionId || '',
        recipientId: queryParams?.recipientId || '',
        productId: queryParams?.productId || '',
    });

    const groupedTags = (storyTags?.tags || []).reduce((acc, tag) => {
        if (!acc[tag.categoryName]) acc[tag.categoryName] = [];
        acc[tag.categoryName].push(tag);
        return acc;
    }, {} as Record<string, { id: number; name: string }[]>);

    const handleFilterChange = (name: string, value: unknown) => {
        setPendingFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleTagChange = (category: string) => (event) => {
        const { value } = event.target;
        setSelectedTags((prev) => ({
            ...prev,
            [category]: typeof value === 'string' ? value.split(',').map(Number) : value,
        }));
    };

    const applyAllFilters = () => {
        const newParams: any = { ...pendingFilters, page: 1 };
        const allTagIds = Object.values(selectedTags).flat();
        newParams.tags = allTagIds.length > 0 ? allTagIds.join(',') : '';
        loadStories(newParams);
    };

    const clearAllFilters = () => {
        setPendingFilters({ isPublished: '', statusId: '', occasionId: '', recipientId: '', productId: '' });
        setSelectedTags({});
        dispatch(Stories.clearFilters());
        loadStories({});
    };

    const handleCheckboxChange = (category: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setVisibleFilters((prev) => [...prev, category]);
        } else {
            setVisibleFilters((prev) => prev.filter((c) => c !== category));
        }
    };

    const generalSelect = (key: string, name: keyof typeof pendingFilters, label: string, options: readonly { title?: string; label?: string; value: number | string }[]) => (
        <FormControl key={key} className={classes.select} size="small">
            <InputLabel id={`stories-tags-${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`stories-tags-${name}-label`}
                label={label}
                name={name}
                value={pendingFilters[name]}
                disabled={loading}
                onChange={(event) => handleFilterChange(name, event.target.value)}
            >
                <MenuItem value="">Any</MenuItem>
                {options.map((option) => (
                    <MenuItem key={`${option.value}${option.title ?? option.label}`} value={option.value}>{option.title ?? option.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const generalFilters: Record<string, React.ReactNode> = {
        Published: generalSelect('Published', 'isPublished', 'Published', STORY_PUBLISHED_OPTIONS),
        Status: generalSelect('Status', 'statusId', 'Status', STORY_STATUS_OPTIONS.map((status) => ({ title: status.label, value: status.id }))),
        Occasion: generalSelect('Occasion', 'occasionId', 'Occasion', STORY_OCCASION_OPTIONS),
        Recipient: generalSelect('Recipient', 'recipientId', 'Recipient', STORY_RECIPIENT_OPTIONS),
        Product: generalSelect('Product', 'productId', 'Product', STORY_PRODUCT_OPTIONS),
    };

    const checkboxGrid = (keys: string[]) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '2px 8px', marginBottom: 12 }}>
            {keys.map((key) => (
                <FormControlLabel
                    key={key}
                    control={<Checkbox size="small" checked={visibleFilters.includes(key)} onChange={handleCheckboxChange(key)} name={key} />}
                    label={key}
                />
            ))}
        </div>
    );

    return (
        <>
            <AppDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title="Edit filters"
                description="Choose which filters are shown. Tag categories also become columns of the table."
                maxWidth="sm"
                actions={(
                    <>
                        <div style={{ display: 'flex', gap: 4, marginRight: 'auto' }}>
                            <Button variant="text" size="small" onClick={() => setVisibleFilters([...GENERAL_KEYS, ...Object.keys(groupedTags)])}>Select all</Button>
                            <Button variant="text" size="small" onClick={() => setVisibleFilters([])}>Clear all</Button>
                        </div>
                        <Button variant="contained" onClick={() => setDialogOpen(false)}>Done</Button>
                    </>
                )}
            >
                <div className={classes.sectionLabel}>General filters</div>
                {checkboxGrid(GENERAL_KEYS)}
                <div className={classes.sectionLabel}>Tag categories</div>
                {checkboxGrid(Object.keys(groupedTags))}
            </AppDialog>

            <FilterBar label="Story tag filters">
                <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={() => setDialogOpen(true)}>
                    Edit filters
                </Button>
                {visibleFilters.filter((key) => GENERAL_KEYS.includes(key)).map((key) => generalFilters[key])}
                {visibleFilters
                    .filter((category) => !GENERAL_KEYS.includes(category) && groupedTags[category])
                    .map((category) => (
                        <FormControl key={category} className={classes.select} size="small">
                            <InputLabel id={`${category}-label`}>{category}</InputLabel>
                            <Select
                                labelId={`${category}-label`}
                                multiple
                                value={selectedTags[category] || []}
                                label={category}
                                onChange={handleTagChange(category)}
                                disabled={loading}
                                renderValue={(selected) => `${selected.length} selected`}
                            >
                                {groupedTags[category].map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ))}
                {visibleFilters.length > 0 && (
                    <div className={classes.clear} style={{ display: 'flex', gap: 8 }}>
                        <Button variant="text" size="small" onClick={clearAllFilters} disabled={loading}>Clear</Button>
                        <Button variant="contained" size="small" onClick={applyAllFilters} disabled={loading}>Apply</Button>
                    </div>
                )}
            </FilterBar>
        </>
    );
}
